/**
 * WritingPromptDisplay Component
 *
 * Displays the writing prompt/question with instructions, word limits, and success criteria.
 * Shows prompt details before and during essay writing.
 */

import React, { useState } from 'react';
import { Check, ClipboardList, FileText, Info, Lightbulb, PenLine, Timer } from 'lucide-react';
import './WritingPromptDisplay.css';

const WritingPromptDisplay = ({
  prompt,
  showSuccessCriteria = true,
  compact = false
}) => {
  const [criteriaExpanded, setCriteriaExpanded] = useState(!compact);

  if (!prompt) {
    return null;
  }

  const {
    prompt_text,
    writing_type,
    tier,
    year_level,
    min_words,
    max_words,
    time_limit_minutes,
    success_criteria
  } = prompt;

  // Format writing type for display
  const formatWritingType = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Format complexity tier for display
  const formatComplexity = (tier) => {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  // Get badge color based on writing type
  const getTypeBadgeColor = (type) => {
    const colors = {
      narrative: '#8b5cf6', // purple
      persuasive: '#ef4444', // red
      creative: '#f59e0b', // orange
      informative: '#3b82f6' // blue
    };
    return colors[type] || '#64748b';
  };

  // Get complexity badge color
  const getComplexityColor = (tier) => {
    const colors = {
      foundation: '#10b981', // green
      intermediate: '#f59e0b', // orange
      advanced: '#ef4444', // red
      mastery: '#8b5cf6' // purple
    };
    return colors[tier] || '#64748b';
  };

  return (
    <div className={`prompt-display ${compact ? 'prompt-compact' : ''}`}>
      {/* Header with metadata */}
      <div className="prompt-header">
        <div className="prompt-badges">
          <span
            className="badge badge-type"
            style={{ backgroundColor: getTypeBadgeColor(writing_type) }}
          >
            {formatWritingType(writing_type)}
          </span>
          <span
            className="badge badge-complexity"
            style={{ backgroundColor: getComplexityColor(tier) }}
          >
            {formatComplexity(tier)}
          </span>
          <span className="badge badge-year">
            Year {year_level}
          </span>
        </div>

        <div className="prompt-requirements">
          <div className="requirement-item">
            <span className="requirement-icon"><FileText size={18} /></span>
            <span className="requirement-text">
              {min_words}–{max_words} words
            </span>
          </div>
          {time_limit_minutes && (
            <div className="requirement-item">
              <span className="requirement-icon"><Timer size={18} /></span>
              <span className="requirement-text">
                {time_limit_minutes} minutes
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Main prompt text */}
      <div className="prompt-content">
        <h3 className="prompt-title">Writing Prompt</h3>
        <div className="prompt-text">
          {prompt_text}
        </div>
      </div>

      {/* Success criteria (expandable in compact mode) */}
      {showSuccessCriteria && success_criteria && (
        <div className="prompt-criteria">
          <button
            className="criteria-toggle"
            onClick={() => setCriteriaExpanded(!criteriaExpanded)}
            aria-expanded={criteriaExpanded}
          >
            <span className="criteria-toggle-icon">
              {criteriaExpanded ? '▼' : '▶'}
            </span>
            <span className="criteria-toggle-text">
              Success Criteria
            </span>
          </button>

          {criteriaExpanded && (
            <div className="criteria-content">
              {success_criteria.structure && (
                <div className="criteria-section">
                  <h4 className="criteria-section-title"><ClipboardList size={16} className="inline-icon" /> Structure</h4>
                  <ul className="criteria-list">
                    {success_criteria.structure.map((item, index) => (
                      <li key={index} className="criteria-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {success_criteria.content && (
                <div className="criteria-section">
                  <h4 className="criteria-section-title"><Lightbulb size={16} className="inline-icon" /> Content</h4>
                  <ul className="criteria-list">
                    {success_criteria.content.map((item, index) => (
                      <li key={index} className="criteria-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {success_criteria.language && (
                <div className="criteria-section">
                  <h4 className="criteria-section-title"><PenLine size={16} className="inline-icon" /> Language</h4>
                  <ul className="criteria-list">
                    {success_criteria.language.map((item, index) => (
                      <li key={index} className="criteria-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {success_criteria.conventions && (
                <div className="criteria-section">
                  <h4 className="criteria-section-title"><Check size={16} className="inline-icon" /> Conventions</h4>
                  <ul className="criteria-list">
                    {success_criteria.conventions.map((item, index) => (
                      <li key={index} className="criteria-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Instructions footer */}
      <div className="prompt-footer">
        <p className="prompt-instruction">
          <Info size={16} className="inline-icon" /> Read the prompt carefully and plan your response before you begin writing.
        </p>
      </div>
    </div>
  );
};

export default WritingPromptDisplay;
